import { Grid, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { passwordRequirements } from '../../utils/userInputUtils';
import { PasswordRequirements } from '../PasswordChange/PasswordChange';
import { changeCoachOnboardingPassword, IAuthResponse } from '../../service/api/authApi';
import LabeledInput from '../Common/LabeledInput';
import StyledButton from '../Common/StyledButton';

type Props = {
  onSuccess: () => void;
};

function CoachOnboardingPasswordBlock({ onSuccess }: Props) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [allValid, setAllValid] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const { search } = useLocation();

  const user = {
    firstName: new URLSearchParams(search).get('name')?.split('-')[0] || '',
    lastName: new URLSearchParams(search).get('name')?.split('-')[1] || '',
    email: decodeURIComponent(new URLSearchParams(search).get('email') || ''),
    approvalId: new URLSearchParams(search).get('approvalId') || '',
    profilePic: decodeURIComponent(new URLSearchParams(search).get('profilePic') || ''),
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
  };

  const onPasswordSubmit = async () => {
    try {
      setLoading(true);
      const response = await changeCoachOnboardingPassword(user.email, password, user.approvalId);
      if (response.status === 200) {
        setLoading(false);
        onSuccess();
      } else {
        setLoading(false);
        const data = (response as unknown as IAuthResponse).json();
        toast.error(
          data.message || 'Error occurred when setting up your password. Please try again!'
        );
      }
    } catch (err: unknown) {
      setLoading(false);
      if (err instanceof Error) {
        toast.error(
          err.message || 'Error occurred when setting up your password. Please try again!'
        );
      }
    }
  };

  useEffect(() => {
    const isAllValid = passwordRequirements(password, confirmPassword).every((requirement) =>
      requirement.isValid()
    );

    setAllValid(isAllValid);
  }, [password, confirmPassword]);

  return (
    <>
      <Typography variant="title2Bold" textAlign="center" color="primary">
        {t('coachOnboarding.createAccount')}
      </Typography>
      <Grid display="flex" flexDirection="column" alignItems="center" gap="16px">
        <Grid
          sx={{
            borderRadius: '50%',
            backgroundColor: 'secondary.main',
            color: 'white',
            width: '72px',
            height: '72px',
            fontSize: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          component="img"
          src={
            user.profilePic
              ? `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${user.profilePic}`
              : '/assets/images/empty-profile.jpg'
          }
        />
        <Grid gap="4px" display="flex" flexDirection="column">
          <Typography variant="title3Regular" textAlign="center" color="primary">
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="bodyRegular" textAlign="center" color="primary.dark">
            {user.email}
          </Typography>
        </Grid>
      </Grid>
      <Grid sx={{ width: '100%', '& > *:not(:last-child)': { mb: '14px' } }}>
        <LabeledInput
          label={t('auth.password')}
          required
          name="newPassword"
          placeholder={t('auth.password')}
          type={showNewPassword ? 'text' : 'password'}
          id="new-password"
          value={password}
          handleChange={handlePasswordChange}
          startIcon={<img src="/assets/icons/security.svg" alt="email" />}
          endIcon={
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowNewPassword}
            >
              {showNewPassword ? (
                <img src="/assets/icons/visibility.svg" alt="visibility" />
              ) : (
                <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
              )}
            </IconButton>
          }
        />
        <LabeledInput
          label={t('changePassword.confirmPassword')}
          required
          name="confirmPassword"
          placeholder={t('changePassword.confirmPassword')}
          type={showConfirmPassword ? 'text' : 'password'}
          id="confirm-password"
          value={confirmPassword}
          handleChange={handleConfirmPasswordChange}
          startIcon={<img src="/assets/icons/security.svg" alt="email" />}
          endIcon={
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowConfirmPassword}
            >
              {showConfirmPassword ? (
                <img src="/assets/icons/visibility.svg" alt="visibility" />
              ) : (
                <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
              )}
            </IconButton>
          }
        />
      </Grid>
      <Grid
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        sx={{ width: '100%' }}
      >
        <Typography variant="bodyBold" textAlign="start" fontWeight="500" color="primary">
          {t('coachOnboarding.passwordConditions')}
        </Typography>
        <Grid display="flex" justifyContent="space-between">
          <PasswordRequirements
            requirements={passwordRequirements(password, confirmPassword).slice(0, 3)}
          />
          <PasswordRequirements
            requirements={passwordRequirements(password, confirmPassword).slice(3, 6)}
          />
        </Grid>
      </Grid>
      <StyledButton disabled={!allValid} fullWidth onClick={onPasswordSubmit} loading={loading}>
        {t('coachOnboarding.createAccount')}
      </StyledButton>
    </>
  );
}

export default CoachOnboardingPasswordBlock;
